import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ProductSearchResult } from '../services/products/products.service';

export type SortOption = 'relevancia' | 'precio_asc' | 'precio_desc' | 'nombre' | 'mas_vendidos';

export interface FilterState {
  busqueda: string;
  categoria: string;
  precioMin: number;
  precioMax: number;
  soloDisponibles: boolean;
  orden: SortOption;
}

const DEBOUNCE_MS = 300;

export function useProductFilters(categoriasDisponibles?: string[]) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parsear estado inicial desde URL
  const parseInitial = useCallback(<T>(key: string, defaultVal: T, parser?: (v: string) => T): T => {
    const val = searchParams.get(key);
    if (val === null) return defaultVal;
    if (parser) {
      try { return parser(val); } catch { return defaultVal; }
    }
    return val as unknown as T;
  }, [searchParams]);

  const [busqueda, setBusqueda] = useState(() => parseInitial('search', ''));
  const [categoria, setCategoria] = useState(() => parseInitial('cat', ''));
  const [precioMin, setPrecioMin] = useState(() => parseInitial('pmin', 0, Number));
  const [precioMax, setPrecioMax] = useState(() => parseInitial('pmax', 500000, Number));
  const [soloDisponibles, setSoloDisponibles] = useState(() => parseInitial('disp', false, v => v === '1'));
  const [orden, setOrden] = useState<SortOption>(() => parseInitial('sort', 'relevancia') as SortOption);

  // Debounce para búsqueda
  const [debouncedBusqueda, setDebouncedBusqueda] = useState(busqueda);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedBusqueda(busqueda), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [busqueda]);

  // Sincronizar filtros con URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedBusqueda) params.set('search', debouncedBusqueda);
    if (categoria) params.set('cat', categoria);
    if (precioMin > 0) params.set('pmin', String(precioMin));
    if (precioMax < 500000) params.set('pmax', String(precioMax));
    if (soloDisponibles) params.set('disp', '1');
    if (orden !== 'relevancia') params.set('sort', orden);
    setSearchParams(params, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedBusqueda, categoria, precioMin, precioMax, soloDisponibles, orden]);

  const filtrar = useCallback((productos: ProductSearchResult[]) => {
    return productos.filter(p => {
      const term = debouncedBusqueda.toLowerCase().trim();
      const matchBusqueda = !term ||
        p.nombre.toLowerCase().includes(term) ||
        (p.codigoBarras?.toLowerCase().includes(term) ?? false) ||
        (p.variante?.toLowerCase().includes(term) ?? false);
      const matchCategoria = !categoria || p.categoria === categoria;
      const precioPrincipal = getPrecioPrincipal(p);
      const matchPrecioMin = precioMin <= 0 || precioPrincipal >= precioMin;
      const matchPrecioMax = precioMax >= 500000 || precioPrincipal <= precioMax;
      const matchDisponible = !soloDisponibles || getStockPrincipal(p) > 0;
      return matchBusqueda && matchCategoria && matchPrecioMin && matchPrecioMax && matchDisponible;
    });
  }, [debouncedBusqueda, categoria, precioMin, precioMax, soloDisponibles]);

  const ordenar = useCallback((productos: ProductSearchResult[]) => {
    const sorted = [...productos];
    switch (orden) {
      case 'precio_asc':
        sorted.sort((a, b) => getPrecioPrincipal(a) - getPrecioPrincipal(b));
        break;
      case 'precio_desc':
        sorted.sort((a, b) => getPrecioPrincipal(b) - getPrecioPrincipal(a));
        break;
      case 'nombre':
        sorted.sort((a, b) => a.nombre.localeCompare(b.nombre));
        break;
      case 'mas_vendidos':
        // Sin datos de ventas, fallback a stock descendente como proxy
        sorted.sort((a, b) => getStockPrincipal(b) - getStockPrincipal(a));
        break;
      default:
        // relevancia: productos con stock primero, luego por nombre
        sorted.sort((a, b) => {
          const stockA = getStockPrincipal(a);
          const stockB = getStockPrincipal(b);
          if (stockB > 0 && stockA <= 0) return 1;
          if (stockA > 0 && stockB <= 0) return -1;
          return a.nombre.localeCompare(b.nombre);
        });
    }
    return sorted;
  }, [orden]);

  const contarActivos = useMemo(() => {
    let count = 0;
    if (debouncedBusqueda) count++;
    if (categoria) count++;
    if (precioMin > 0) count++;
    if (precioMax < 500000) count++;
    if (soloDisponibles) count++;
    if (orden !== 'relevancia') count++;
    return count;
  }, [debouncedBusqueda, categoria, precioMin, precioMax, soloDisponibles, orden]);

  const reset = useCallback(() => {
    setBusqueda('');
    setCategoria('');
    setPrecioMin(0);
    setPrecioMax(500000);
    setSoloDisponibles(false);
    setOrden('relevancia');
  }, []);

  return {
    // Raw values (para inputs controlados)
    busqueda,
    categoria,
    precioMin,
    precioMax,
    soloDisponibles,
    orden,
    // Setters
    setBusqueda,
    setCategoria,
    setPrecioMin,
    setPrecioMax,
    setSoloDisponibles,
    setOrden,
    // Computed
    debouncedBusqueda,
    contarActivos,
    reset,
    // Functions
    filtrar,
    ordenar,
  };
}

export function getPrecioPrincipal(producto: ProductSearchResult): number {
  return producto.precio ?? producto.presentaciones?.[0]?.precio ?? 0;
}

export function getStockPrincipal(producto: ProductSearchResult): number {
  return producto.stock ?? producto.presentaciones?.[0]?.stock ?? 0;
}
